import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Undo2, AlertTriangle, Package, RefreshCw, Clock, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ComboReversal = () => {
  const navigate = useNavigate();
  
  // Eligible combos
  const [combos, setCombos] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Reversal history
  const [reversals, setReversals] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Stats
  const [stats, setStats] = useState({
    total_reversals: 0,
    reversals_today: 0,
    top_reasons: []
  });
  
  // Reversal dialog
  const [reversalDialogOpen, setReversalDialogOpen] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [reversalReason, setReversalReason] = useState('');
  const [reversing, setReversing] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('eligible');

  const fetchEligibleCombos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      
      const response = await apiClient.get('/combo/reversal/eligible-combos', { params });
      setCombos(response.data.combos || []);
    } catch (error) {
      console.error('Error fetching eligible combos:', error);
      toast.error('Failed to fetch combos');
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchReversalHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const response = await apiClient.get('/combo/reversal/history', {
        params: { limit: 100 }
      });
      setReversals(response.data.reversals || []);
    } catch (error) {
      console.error('Error fetching reversal history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/combo/reversal/stats');
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    if (activeTab === 'eligible') {
      fetchEligibleCombos();
    } else {
      fetchReversalHistory();
    }
  }, [activeTab, fetchEligibleCombos, fetchReversalHistory, fetchStats]);

  const openReversalDialog = async (combo) => {
    try {
      // Fetch full details
      const response = await apiClient.get(`/combo/reversal/combo-details/${combo.combo_usn}`);
      setSelectedCombo(response.data);
      setReversalReason('');
      setReversalDialogOpen(true);
    } catch (error) {
      console.error('Error fetching combo details:', error);
      toast.error('Failed to fetch combo details');
    }
  };

  const handleReversal = async () => {
    if (!reversalReason || reversalReason.trim().length < 3) {
      toast.error('Please provide a reversal reason (min 3 characters)');
      return;
    }
    
    setReversing(true);
    try {
      const response = await apiClient.post('/combo/reversal/reverse-combo', {
        combo_usn: selectedCombo.combo.combo_usn,
        reason: reversalReason.trim()
      });
      
      toast.success(`Combo ${response.data.combo_usn} reversed successfully`);
      setReversalDialogOpen(false);
      fetchEligibleCombos();
      fetchStats();
    } catch (error) {
      console.error('Reversal error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reverse combo');
    } finally {
      setReversing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ASSEMBLED': return <Badge variant="secondary">Assembled</Badge>;
      case 'PENDING_PUT_AWAY': return <Badge className="bg-yellow-100 text-yellow-800">Pending Put Away</Badge>;
      case 'IN_STORAGE': return <Badge className="bg-green-100 text-green-800">In Storage</Badge>;
      case 'REVERSED': return <Badge variant="destructive">Reversed</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="combo-reversal-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Undo2 className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Combo Reversal</h1>
                <p className="text-sm text-[#E8DCC8]">Disassemble combo products</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => { fetchStats(); fetchEligibleCombos(); fetchReversalHistory(); }} className="text-gray-800">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Reversals</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total_reversals}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Reversals Today</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-orange-600">{stats.reversals_today}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Top Reason</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold truncate">
                {stats.top_reasons?.[0]?.reason || '-'}
              </p>
              {stats.top_reasons?.[0]?.count && (
                <p className="text-sm text-gray-500">{stats.top_reasons[0].count} times</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="eligible" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Eligible Combos
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Reversal History
            </TabsTrigger>
          </TabsList>

          {/* Eligible Combos Tab */}
          <TabsContent value="eligible">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <CardTitle>Eligible for Reversal</CardTitle>
                    <CardDescription>Combos that can be reversed (not yet picked/sold)</CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by USN..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10 w-64"
                      data-testid="combo-reversal-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : combos.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">No eligible combos found.</p>
                    <p className="text-sm text-gray-400">
                      {search ? 'Try a different search term.' : 'Assemble some combos first.'}
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Combo USN</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Design / Size / Shade</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assembled At</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {combos.map((combo) => (
                        <TableRow key={combo.combo_usn}>
                          <TableCell className="font-mono text-sm">{combo.combo_usn}</TableCell>
                          <TableCell className="font-medium">{combo.combo_name}</TableCell>
                          <TableCell>
                            {combo.design_name || '-'} / {combo.size || '-'} / {combo.shade || '-'}
                          </TableCell>
                          <TableCell>{getStatusBadge(combo.status)}</TableCell>
                          <TableCell className="text-sm">
                            {combo.assembled_at ? new Date(combo.assembled_at).toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openReversalDialog(combo)}
                              data-testid={`reverse-${combo.combo_usn}`}
                            >
                              <Undo2 className="w-4 h-4 mr-1" />
                              Reverse
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reversal History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Reversal History</CardTitle>
                <CardDescription>Log of all combo reversals</CardDescription>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : reversals.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">No reversals recorded yet.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reversal ID</TableHead>
                        <TableHead>Combo USN</TableHead>
                        <TableHead>Combo Name</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Reversed By</TableHead>
                        <TableHead>Reversed At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reversals.map((rev) => (
                        <TableRow key={rev.reversal_id}>
                          <TableCell className="font-mono text-xs">{rev.reversal_id}</TableCell>
                          <TableCell className="font-mono text-sm">{rev.combo_usn}</TableCell>
                          <TableCell>{rev.combo_name}</TableCell>
                          <TableCell className="max-w-xs truncate" title={rev.reason}>
                            {rev.reason}
                          </TableCell>
                          <TableCell className="text-sm">{rev.reversed_by}</TableCell>
                          <TableCell className="text-sm">
                            {new Date(rev.reversed_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reversal Dialog */}
      <Dialog open={reversalDialogOpen} onOpenChange={setReversalDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Reverse Combo
            </DialogTitle>
            <DialogDescription>
              This will mark the combo as reversed. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCombo?.combo && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Combo USN</p>
                    <p className="font-mono text-sm">{selectedCombo.combo.combo_usn}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-medium">{selectedCombo.combo.combo_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Status</p>
                    {getStatusBadge(selectedCombo.combo.status)}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Bin Location</p>
                    <p>{selectedCombo.combo.put_away_bin || 'Not stored'}</p>
                  </div>
                </div>
              </div>
              
              {selectedCombo.components?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Components to be released:</p>
                  <div className="space-y-1">
                    {selectedCombo.components.map((comp, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-orange-50 p-2 rounded">
                        <span>{comp.component_name || comp.component_barcode}</span>
                        <span className="text-gray-500">x{comp.quantity || 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <Label>Reversal Reason *</Label>
                <Textarea
                  value={reversalReason}
                  onChange={(e) => setReversalReason(e.target.value)}
                  placeholder="Why is this combo being reversed?"
                  rows={3}
                  data-testid="reversal-reason-input"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 3 characters required</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReversalDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReversal}
              disabled={reversing || !reversalReason || reversalReason.trim().length < 3}
              data-testid="confirm-reversal-btn"
            >
              {reversing ? 'Reversing...' : 'Confirm Reversal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComboReversal;
